import store from '@/store'
import axios from 'axios'
import CommonGetters from '@/store/common/common-getters'
import CommonActions from '@/store/common/common-actions'
import Config from '@/constants/config'
import PopupService from '@/services/popup.service'
import SpinnerService from '@/services/spinner.service'
import FilteringService from '@/services/filtering.service'
import Keys from '@/constants/keys'
import MemoryService from '@/services/memory.service'
import VisualisationService from '@/services/visualisation.service'
import UtilsService from '@/services/utils.service'

const SEARCH_PAGE_SIZE = Number(Config.search.shortQueryMax || 10000);
let availableSearchSession = {
    query: '',
    pairsKey: '',
    includeFilters: true,
    tool: 'ovt',
    offset: 0,
    total: 0,
    hasMore: false,
    loading: false,
    requestId: 0,
};

const ObjectsService = {
    findAsteroidByPhraseUrl() {
        return Config.api.restUrl + Config.api.findAsteroidByPhraseUrl;
    },

    findAsteroidByPhraseInSearchUrl() {
        return Config.api.restUrl + Config.api.findAsteroidByPhraseInSearchUrl;
    },

    findCountAsteroidByFiltersUrl() {
        return Config.api.restUrl + Config.api.findCountAsteroidByFiltersUrl;
    },

    findAsteroidByNameUrl() {
        return Config.api.restUrl + Config.api.findAsteroidByNameUrl;
    },

    initialLoadPerformed() {
        return store.getters[CommonGetters.objectsInitialLoadPerformed];
    },

    getObjects() {
        return store.getters[CommonGetters.objects];
    },

    getObjectsSelected() {
        return store.getters[CommonGetters.objectsSelected];
    },

    getObjectsFilteredSelected() {
        const objects = ObjectsService.getObjects();
        const objectsSelected = ObjectsService.getObjectsSelected();
        const objectsFilteredSelected = [];

        if (!objects || !objects.list || !objects.list.length || !objectsSelected.length) return;

        for (const objectName of objectsSelected) {
            const object = objects.list.filter(obj => obj.name === objectName);
            if (object.length) {
                objectsFilteredSelected.push(object[0]);
            }
        }

        return objectsFilteredSelected;
    },

    setObjects(objects, addSelected = false) {
        store.dispatch(CommonActions.setObjects, objects);

        if (addSelected && objects.list && objects.list.length) {
            const selected = objects.list.map(obj => obj.name);
            ObjectsService.setObjectsSelected(selected);
        } else {
            ObjectsService.setObjectsSelected([]);
        }
    },

    concatObjects(objects, addSelected = false) {
        const currentObjects = ObjectsService.getObjects();
        const mergedObjectsArray = [...currentObjects.list, ...objects.list];
        currentObjects.list = mergedObjectsArray;

        store.dispatch(CommonActions.setObjects, currentObjects);

        if (addSelected && objects.list && objects.list.length) {
            const selected = [...ObjectsService.getObjectsSelected(), ...objects.list.map(obj => obj.name)];
            ObjectsService.setObjectsSelected(selected);
        }
    },

    setObjectsInitialLoadPerformed(value) {
        store.dispatch(CommonActions.setObjectsInitialLoadPerformed, value);
    },

    setObjectsSelected(objects) {
        store.dispatch(CommonActions.setObjectsSelected, objects);
    },

    handleSelect(objectId) {
        let selected = [ ...ObjectsService.getObjectsSelected() ];
        if (!selected.includes(objectId)) {
            selected.push(objectId);
        } else {
            selected = selected.filter(id => id !== objectId);
        }
        ObjectsService.setObjectsSelected(selected);
    },

    selectAll() {
        const objects = ObjectsService.getObjects().list;
        if (!objects || !objects.length) return;

        const selected = [];
        for (const object of objects) {
            if (!object.error) selected.push(object.name);
        }

        ObjectsService.setObjectsSelected(selected);
    },

    getConfigurationPopupMode() {
        return store.getters[CommonGetters.configurationPopupMode];
    },

    setConfigurationPopupMode(value) {
        store.dispatch(CommonActions.setConfigurationPopupMode, value);
    },

    getUserDefinedObjects() {
        return store.getters[CommonGetters.userDefinedObjects];
    },

    setUserDefinedObjects(value) {
        store.dispatch(CommonActions.setUserDefinedObjects, value);
    },

    selectNone() {
        ObjectsService.setObjectsSelected([]);
    },

    getLoadingAsteroidsList() {
        return store.getters[CommonGetters.loadingAsteroidsList];
    },

    getAvailableAsteroidsTotal() {
        return store.getters[CommonGetters.availableAsteroidsTotal];
    },

    getAvailableAsteroidsHasMore() {
        return store.getters[CommonGetters.availableAsteroidsHasMore];
    },

    setLoadingAsteroidsList(value) {
        store.dispatch(CommonActions.setLoadingAsteroidsList, value);
    },

    getOptRoute(){
        return store.getters[CommonGetters.optRoute];
    },

    setOptRoute(value){
        return store.dispatch(CommonActions.setOptRoute, value);
    },

    getAvailableAsteroidsList() {
        return store.getters[CommonGetters.availableAsteroidsList];
    },

    setAvailableAsteroidsList(value) {
        store.dispatch(CommonActions.setAvailableAsteroidsList, value);
    },

    setAvailableAsteroidsTotal(value) {
        store.dispatch(CommonActions.setAvailableAsteroidsTotal, value);
    },

    setAvailableAsteroidsHasMore(value) {
        store.dispatch(CommonActions.setAvailableAsteroidsHasMore, value);
    },

    resetAvailableAsteroidsSearchSession() {
        availableSearchSession = {
            query: '',
            pairsKey: '',
            includeFilters: true,
            tool: 'ovt',
            offset: 0,
            total: 0,
            hasMore: false,
            loading: false,
            requestId: availableSearchSession.requestId,
        };
    },

    getSearchSourceTypePairs() {
        const showAsteroids = VisualisationService.getProperty("showAsteroids");
        const showComets = VisualisationService.getProperty("showComets");
        const showNEOCP = VisualisationService.getProperty("showNEOCP");
        const showNEOs = VisualisationService.getProperty("showNEOs");
        const showNonNEOs = VisualisationService.getProperty("showNonNEOs");

        const enabledTypes = new Set();
        if (showAsteroids) enabledTypes.add(1);
        if (showComets) enabledTypes.add(3);
        if (showNEOCP) enabledTypes.add(2);

        if (enabledTypes.size === 0) {
            return [];
        }

        const requestedCodes = new Set();
        if (showNEOs) requestedCodes.add("NEO");
        if (showNonNEOs) requestedCodes.add("non-NEO");

        if (requestedCodes.size === 0 && !showNEOCP) {
            return [];
        }

        const pairs = new Set();
        if (requestedCodes.size > 0) {
            for (const typeId of [1, 3]) {
                if (!enabledTypes.has(typeId)) continue;
                for (const c of requestedCodes) {
                    pairs.add(`${typeId}:${c}`);
                }
            }
        }

        if (enabledTypes.has(2)) {
            pairs.add(`2:NEO`);
        }

        return [...pairs];
    },

    getFinalAsteroidsList() {
        return store.getters[CommonGetters.finalAsteroidsList];
    },

    setFinalAsteroidsList(value) {
        store.dispatch(CommonActions.setFinalAsteroidsList, value);
    },

    getInsertAsteroidsList() {
        return store.getters[CommonGetters.insertAsteroidsList];
    },

    setInsertAsteroidsList(value) {
        store.dispatch(CommonActions.setInsertAsteroidsList, value);
    },

    getAsteroidsNames() {
        return store.getters[CommonGetters.asteroidsNames];
    },

    setAsteroidsNames(value) {
        store.dispatch(CommonActions.setAsteroidsNames, value);
    },

    setSkychartEnableVisualisation(value) {
        store.dispatch(CommonActions.setSkychartEnableVisualisation, value);
    },

    updateAsteroidsList(availableAsteroidsList = null, finalAsteroidsList = null) {
        const availableAsteroids = availableAsteroidsList || ObjectsService.getAvailableAsteroidsList();
        const finalAsteroids = finalAsteroidsList || ObjectsService.getFinalAsteroidsList();
        ObjectsService.setAvailableAsteroidsList(availableAsteroids);
        ObjectsService.setFinalAsteroidsList(finalAsteroids);
        const asteroidsNames = [];
        for (const item of finalAsteroids) {
            asteroidsNames.push(item.name);
        }
        ObjectsService.setSkychartEnableVisualisation(asteroidsNames.length > 0);
        ObjectsService.setAsteroidsNames(asteroidsNames.join());
    },

    objectConfigurationAdd(availableSelected) {
        let finalAsteroidsList = [...ObjectsService.getFinalAsteroidsList(), ...availableSelected];
        let availableAsteroidsList = [...ObjectsService.getAvailableAsteroidsList()];
        for (const item of availableSelected) {
            availableAsteroidsList = availableAsteroidsList.filter(e => e.name !== item.name);
        }
        ObjectsService.updateAsteroidsList(availableAsteroidsList, finalAsteroidsList);
        return [];
    },

    objectConfigurationAddAll() {
        const finalAsteroidsList = [...ObjectsService.getFinalAsteroidsList()];
        for (const item of ObjectsService.getAvailableAsteroidsList()) {
            finalAsteroidsList.push(item);
        }
        ObjectsService.updateAsteroidsList([], finalAsteroidsList);
        return [];
    },

    objectConfigurationRemove(finalSelected) {
        let finalAsteroidsList = [...ObjectsService.getFinalAsteroidsList()];
        const finalSelectedFilteredList = [...this.filterAvailableAsteroids(finalSelected)];
        let availableAsteroidsList = [...ObjectsService.getAvailableAsteroidsList(), ...finalSelectedFilteredList];
        for (const item of finalSelected) {
            finalAsteroidsList = finalAsteroidsList.filter(e => e.name !== item.name);
        }        
        ObjectsService.updateAsteroidsList(availableAsteroidsList, finalAsteroidsList);        
        return [];
    },

    objectConfigurationRemoveAll() {
        const availableAsteroidsList = [...ObjectsService.getAvailableAsteroidsList()];
        const finalSelectedFilteredList = [...this.filterAvailableAsteroids(ObjectsService.getFinalAsteroidsList())];
        for (const item of finalSelectedFilteredList) {
            availableAsteroidsList.push(item);
        }        
        ObjectsService.updateAsteroidsList(availableAsteroidsList, []);
        return [];
    },

    extractObjectsNames(objectsList) {
        const asteroidsNames = [];
        for (const item of objectsList) {
            asteroidsNames.push(item.name);
        }
        return asteroidsNames.join();
    },

    findAsteroidByPhraseThroughURL(phrase = '', autoAddObject = false, tool = 'ovt') {
        if (!phrase || phrase === '') {
            ObjectsService.setAvailableAsteroidsList([]);
            return;
        }
        ObjectsService.setLoadingAsteroidsList(true);

        // --- NORMALIZE INPUT ---
        // FIX #1: make splitting reliable across environments (DEV/OPE).
        // - collapse multiple spaces
        // - treat %20 or + as spaces
        const raw = String(phrase)
            .replace(/%20|\+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        const upper = raw.toUpperCase();
        const parts = upper.split(' ');
        const hasSpace = parts.length >= 2;
        const head = parts[0] || '';
        const tail = hasSpace ? parts.slice(1).join(' ') : '';
        const leadingInt = parseInt(head, 10);

        const headIsPureInt = /^\d+$/.test(head);

        const isDigitsOnly = /^\d+$/.test(raw); // for pure numeric queries like "7"

        const hasSlash = raw.includes('/');

        const url = ObjectsService.findAsteroidByPhraseUrl() + '?query=' + encodeURIComponent(phrase);
        //console.log('Fetching from URL:', url);

        axios(url)
            .then(response => {
                const finalAsteroidsList = ObjectsService.getFinalAsteroidsList();

                // Build the initial list (avoid duplicates already in scene by number OR name)
                let availableAsteroidsList = [];
                for (const item of response.data) {
                    const alreadyInScene = finalAsteroidsList.some(e =>
                        (e.number && item.number && Number(e.number) === Number(item.number)) ||
                        ((e.name || '').toUpperCase() === (item.name || '').toUpperCase())
                    );
                    if (!alreadyInScene) availableAsteroidsList.push(item);
                }

                // Save for UI (autocomplete etc.)
                ObjectsService.setAvailableAsteroidsList(availableAsteroidsList);

                if (!autoAddObject) return;

                // ------------------ STRICT, SIMPLE MATCHING ------------------
                // FIX #2: Only two valid forms:
                //   A) "Number Name"  -> exact number AND exact name
                //   B) "Designation"  -> exact designation (or exact name), OR exact number if digits-only
                const norm = s => (s || '').replace(/\s+/g, ' ').trim().toUpperCase();

                // Helper: EXACT pickers over a list
                const pickExactNumberName = (list, num, nameUpper) =>
                    list.filter(e => Number(e.number) === num && norm(e.name) === nameUpper);

                const pickExactDesignation = (list, dUpper) =>
                    list.filter(e => norm(e.designator) === dUpper || norm(e.name) === dUpper);

                const pickExactNumberOnly = (list, num) =>
                    list.filter(e => Number(e.number) === num);

                let candidates = [];

                if (hasSlash) {
                    const fullNorm = norm(raw);
                    candidates = pickExactDesignation(availableAsteroidsList, fullNorm);
                }

                // Only run the other branches if we still have no candidates
                if (!candidates.length) {
                    if (hasSpace && headIsPureInt) {
                        // FORM A: "Number Name"
                        // Accept only exact (number == leadingInt) AND exact name == tail
                        candidates = pickExactNumberName(availableAsteroidsList, leadingInt, tail);

                        // Also accept exact "NUMBER NAME" in designator if present
                        if (candidates.length === 0) {
                            // CHANGE: use the same normalization on the whole query
                            const fullNorm = norm(raw); // e.g., "2 PALLAS"
                            candidates = availableAsteroidsList.filter(e =>
                                norm(e.designator) === fullNorm
                            );
                        }
                    } else {
                        // FORM B: "Designation" (single token like 2024YR4) OR pure number like "7"
                        if (isDigitsOnly) {
                            // Exactly the asteroid with that number
                            candidates = pickExactNumberOnly(availableAsteroidsList, Number(raw));
                        } else {
                            // Exact designation (or exact name)
                            candidates = pickExactDesignation(availableAsteroidsList, norm(raw));
                        }
                    }
                }

                // If still none, try selecting an EXACT match already loaded in scene.
                if (!candidates.length && finalAsteroidsList.length) {
                    if (hasSlash) {
                        const fullNorm = norm(raw);
                        candidates = pickExactDesignation(finalAsteroidsList, fullNorm);
                    }

                    if (!candidates.length) {
                        if (hasSpace && headIsPureInt) {
                            candidates = pickExactNumberName(finalAsteroidsList, leadingInt, tail);
                            if (!candidates.length) {
                                const fullNorm = norm(raw);
                                candidates = finalAsteroidsList.filter(e =>
                                norm(e.designator) === fullNorm
                                );
                            }
                        } else {
                            if (isDigitsOnly) {
                                candidates = pickExactNumberOnly(finalAsteroidsList, Number(raw));
                            } else {
                                candidates = pickExactDesignation(finalAsteroidsList, norm(raw));
                            }
                        }
                    }

                    // FIX #3: if the exact target is already loaded, SELECT it
                    if (candidates.length >= 1) {
                        const chosenLoaded = candidates[0];
                        const designator = (chosenLoaded.number && Number(chosenLoaded.number) !== 0)
                            ? String(chosenLoaded.number)
                            : (chosenLoaded.designator || chosenLoaded.name || '');

                        if (tool === 'ovt') {
                            VisualisationService.selectObject(designator);
                        } else if (tool === 'sovt') {
                            setTimeout(() => {
                                VisualisationService.setSelectedSynodicObjectName(designator);
                            });
                        } else if (tool === 'fvt') {
                            VisualisationService.searchCloseApproaches();
                        }
                        return;
                    }
                }

                // If multiple exact candidates (should not happen): prefer exact Number+Name match over others.
                if (candidates.length > 1) {
                    if (hasSpace && headIsPureInt) {
                        const narrowed = pickExactNumberName(candidates, leadingInt, tail);
                        if (narrowed.length === 1) candidates = narrowed;
                    }
                }

                if (!candidates.length) {
                    if (hasSlash) {
                        throw new Error(`Load through URL: Object "${phrase}" was not found.`);
                    }
                    throw new Error(`Load through URL: Object "${phrase}" was not found (requires exact Number+Name or exact Designation).`);
                }

                // Add + select the chosen one
                const chosen = candidates[0];
                ObjectsService.objectConfigurationAdd([chosen]);
                ObjectsService.setAvailableAsteroidsList([]);

                const designator = (chosen.number && Number(chosen.number) !== 0)
                    ? String(chosen.number)
                    : (chosen.designator || chosen.name || '');

                if (tool === 'ovt') {
                    VisualisationService.selectObject(designator);
                } else if (tool === 'sovt') {
                    setTimeout(() => {
                        VisualisationService.setSelectedSynodicObjectName(designator);
                    });
                } else if (tool === 'fvt') {
                    VisualisationService.searchCloseApproaches();
                }
            })
            .catch(error => {
                const message = UtilsService.prepareErrorMessage(error);
                PopupService.show({ component: 'PopupInfo', type: 'error', message });
            })
            .finally(() => {
                ObjectsService.setLoadingAsteroidsList(false);
            });
    },

    findAsteroidByPhrase(phrase = '', autoAddObject = false, tool = 'ovt', options = {}) {
        const append = !!(options && options.append);
        const includeFilters =
            options && Object.prototype.hasOwnProperty.call(options, 'includeFilters')
                ? !!options.includeFilters
                : true;
        if (!phrase || phrase === '') {
            ObjectsService.setAvailableAsteroidsList([]);
            ObjectsService.setAvailableAsteroidsTotal(0);
            ObjectsService.setAvailableAsteroidsHasMore(false);
            ObjectsService.resetAvailableAsteroidsSearchSession();
            return;
        }
        const pairs = includeFilters ? ObjectsService.getSearchSourceTypePairs() : [];
        if (includeFilters && !pairs.length) {
            ObjectsService.setAvailableAsteroidsList([]);
            ObjectsService.setAvailableAsteroidsTotal(0);
            ObjectsService.setAvailableAsteroidsHasMore(false);
            ObjectsService.resetAvailableAsteroidsSearchSession();
            return;
        }

        const pairsKey = includeFilters ? pairs.slice().sort().join('|') : '__ALL__';
        const normalizedPhrase = String(phrase).trim();
        if (
            !append ||
            availableSearchSession.query !== normalizedPhrase ||
            availableSearchSession.pairsKey !== pairsKey ||
            availableSearchSession.includeFilters !== includeFilters
        ) {
            availableSearchSession.query = normalizedPhrase;
            availableSearchSession.pairsKey = pairsKey;
            availableSearchSession.includeFilters = includeFilters;
            availableSearchSession.tool = tool || 'ovt';
            availableSearchSession.offset = 0;
            availableSearchSession.total = 0;
            availableSearchSession.hasMore = false;
        } else if (tool) {
            availableSearchSession.tool = tool;
        } else if (availableSearchSession.loading || !availableSearchSession.hasMore) {
            return;
        }

        const requestOffset = append ? availableSearchSession.offset : 0;
        availableSearchSession.loading = true;
        availableSearchSession.requestId += 1;
        const requestId = availableSearchSession.requestId;
        ObjectsService.setLoadingAsteroidsList(true);

        const params = new URLSearchParams()
        params.set('query', normalizedPhrase)
        params.set('shortQueryLength', Config.search.shortQueryLength)
        params.set('shortQueryMax', Config.search.shortQueryMax)
        params.set('offset', requestOffset)
        params.set('limit', SEARCH_PAGE_SIZE)
        if (includeFilters) {
            for (const p of pairs) {
                params.append('sourceTypeCode', p)
            }
        }

        const url =
            ObjectsService.findAsteroidByPhraseInSearchUrl() +
            '?' +
            params.toString()

        axios(url)
            .then(response => {
                if (requestId !== availableSearchSession.requestId) return;
                const payload = response && response.data ? response.data : {};
                const responseItems = Array.isArray(payload.items) ? payload.items : [];
                const responseTotal = Number(payload.total || 0);
                const responseNextOffset = Number(payload.nextOffset || (requestOffset + responseItems.length));
                const finalAsteroidsList = ObjectsService.getFinalAsteroidsList();
                const pageItems = [];
                for (const item of responseItems) {
                    if (!finalAsteroidsList.some(e => e.name === item.name)) {
                        pageItems.push(item)
                    }
                }

                let availableAsteroidsList = pageItems;
                if (append) {
                    const existing = ObjectsService.getAvailableAsteroidsList();
                    const merged = [...existing, ...pageItems];
                    const uniqueById = new Map();
                    for (const objectItem of merged) {
                        const key = objectItem.id || `${objectItem.number || ''}_${objectItem.name || ''}`;
                        if (!uniqueById.has(key)) {
                            uniqueById.set(key, objectItem);
                        }
                    }
                    availableAsteroidsList = [...uniqueById.values()];
                }

                ObjectsService.setAvailableAsteroidsList(availableAsteroidsList);
                ObjectsService.setAvailableAsteroidsTotal(responseTotal);
                const calculatedHasMore = availableAsteroidsList.length < responseTotal;
                const payloadHasMore = Boolean(payload.hasMore);
                const hasMore = payloadHasMore || calculatedHasMore;
                ObjectsService.setAvailableAsteroidsHasMore(hasMore);

                availableSearchSession.offset = responseNextOffset;
                availableSearchSession.total = responseTotal;
                availableSearchSession.hasMore = hasMore;

                if (autoAddObject) {
                    let objectsToAdd = [];
                    if (availableAsteroidsList.length === 1) {
                        objectsToAdd = availableAsteroidsList;
                    } else {
                        objectsToAdd = availableAsteroidsList.filter(e => e.name.toUpperCase() == phrase.toUpperCase() || e.number == phrase.toUpperCase());
                    }

                    if (!objectsToAdd.length) {
                        throw new Error(`Object "${phrase}" was not found.`);
                    }

                    ObjectsService.objectConfigurationAdd(objectsToAdd);
                    ObjectsService.setAvailableAsteroidsList([]);
                    const designator = objectsToAdd[0].number ? objectsToAdd[0].number + '' : objectsToAdd[0].name + '';
                    if (tool === 'ovt') {
                        VisualisationService.selectObject(designator);
                    } else if (tool === 'sovt') {
                        setTimeout(() => {
                            VisualisationService.setSelectedSynodicObjectName(designator);
                        })
                    }
                }
            })
            .catch(error => {
                if (requestId !== availableSearchSession.requestId) return;
                const message = UtilsService.prepareErrorMessage(error);
                PopupService.show({
                    component: 'PopupInfo',
                    type: 'error',
                    message
                });
            })
            .finally(() => {
                if (requestId !== availableSearchSession.requestId) return;
                availableSearchSession.loading = false;
                ObjectsService.setLoadingAsteroidsList(false);
            });
	},

    loadMoreAvailableAsteroids() {
        if (!availableSearchSession.query || availableSearchSession.loading || !availableSearchSession.hasMore) {
            return;
        }
        ObjectsService.findAsteroidByPhrase(
            availableSearchSession.query,
            false,
            availableSearchSession.tool || 'ovt',
            { append: true, includeFilters: availableSearchSession.includeFilters }
        );
    },

    findCountAsteroidByPhrase(phrase = '') {
        if (!phrase || phrase.trim() === '') {
            ObjectsService.setAvailableAsteroidsTotal(0)
            ObjectsService.setAvailableAsteroidsHasMore(false)
            return
        }

        const showAsteroids = VisualisationService.getProperty("showAsteroids")
        const showComets    = VisualisationService.getProperty("showComets")
        const showNEOCP     = VisualisationService.getProperty("showNEOCP")
        const showNEOs      = VisualisationService.getProperty("showNEOs")
        const showNonNEOs   = VisualisationService.getProperty("showNonNEOs")

        const params = new URLSearchParams()
        params.set('text', phrase)
        params.set('shortQueryLength', Config.search.shortQueryLength)
        params.set('shortQueryMax', Config.search.shortQueryMax)

        /* =====================================================
            SOURCE TYPES
            ===================================================== */
        const enabledTypes = new Set()
        if (showAsteroids) enabledTypes.add(1) // Asteroids
        if (showComets)    enabledTypes.add(3) // Comets
        if (showNEOCP)     enabledTypes.add(2) // NEOCP

        if (enabledTypes.size === 0) {
            ObjectsService.setAvailableAsteroidsTotal(0)
            ObjectsService.setAvailableAsteroidsHasMore(false)
            return
        }

        /* =====================================================
            CODES — STRICT LOGIC
            ===================================================== */
        const requestedCodes = new Set()

        // User must explicitly choose at least one code
        const userSelectedCodes = showNEOs || showNonNEOs

        if (userSelectedCodes) {
            if (showNEOs)    requestedCodes.add("NEO")
            if (showNonNEOs) requestedCodes.add("non-NEO")
        }
        // If both code toggles are OFF -> requestedCodes stays empty

        /* =====================================================
            If no codes selected and NEOCP is OFF -> nothing to count
            ===================================================== */
        if (requestedCodes.size === 0 && !showNEOCP) {
            ObjectsService.setAvailableAsteroidsTotal(0)
            ObjectsService.setAvailableAsteroidsHasMore(false)
            return
        }

        /* =====================================================
            BUILD OR-PAIRS (sourceTypeCode=ID:CODE)
            ===================================================== */
        const pairs = new Set()

        // Only generate 1:* and 3:* pairs if codes were explicitly selected
        if (requestedCodes.size > 0) {
            for (const typeId of [1, 3]) {
            if (!enabledTypes.has(typeId)) continue
            for (const c of requestedCodes) {
                pairs.add(`${typeId}:${c}`)
            }
            }
        }

        // NEOCP always contributes NEO only
        if (enabledTypes.has(2)) {
            pairs.add(`2:NEO`)
        }

        if (pairs.size === 0) {
            ObjectsService.setAvailableAsteroidsTotal(0)
            ObjectsService.setAvailableAsteroidsHasMore(false)
            return
        }

        /* =====================================================
            BUILD QUERY
            ===================================================== */
        for (const p of pairs) {
            params.append('sourceTypeCode', p)
        }

        const url =
            ObjectsService.findCountAsteroidByFiltersUrl() +
            '?' +
            params.toString()

        axios(url)
            .then(response => {
            if (response && response.data != null) {
                const payload = response.data;
                const isObjectPayload = payload !== null && typeof payload === 'object';
                const total = isObjectPayload ? Number(payload.count || 0) : Number(payload || 0);
                const hasMore = isObjectPayload ? Boolean(payload.hasMore) : false;

                ObjectsService.setAvailableAsteroidsTotal(total)
                ObjectsService.setAvailableAsteroidsHasMore(hasMore)
            } else {
                ObjectsService.setAvailableAsteroidsTotal(0)
                ObjectsService.setAvailableAsteroidsHasMore(false)
            }
            })
            .catch(error => {
            ObjectsService.setAvailableAsteroidsHasMore(false)
            const message = UtilsService.prepareErrorMessage(error)
            PopupService.show({
                component: 'PopupInfo',
                type: 'error',
                message
            })
            })
    },

    filterAvailableAsteroids(availableAsteroidsList) {
        const filteredAsteroidList = availableAsteroidsList.filter(item => {
            // code: NEO black/Non-NEO gray 
            const showCode = this.isCodeAllowed(item.code);

            // sourceType: NEOCP blue
            const showNEOCP = this.isNEOCPAllowed(item.sourceType.name);

            // bodyType.name = Asteroid(id=1)/Comet(id=2)/Unconfirmed(id=3)
            const showBodyType = this.isBodyTypeAllowed(item.bodyType.name); 

            // Return true if all conditions are met
            return showNEOCP && (showCode || item.sourceType.name === 'NEOCP') && showBodyType;
        }); 
        return filteredAsteroidList;
    },

    isCodeAllowed(code) {
        return (code === "NEO" && VisualisationService.getProperty("showNEOs")) ||
               (code === "non-NEO" && VisualisationService.getProperty("showNonNEOs"));
    },
    isNEOCPAllowed(source) {
        return source === 'NEOCP' ? VisualisationService.getProperty("showNEOCP") : true;
    },  
    isBodyTypeAllowed(bodyType) {
        return (bodyType === "Asteroid" && VisualisationService.getProperty("showAsteroids")) ||
        (bodyType === "Comet" && VisualisationService.getProperty("showComets")) ||
        (bodyType === "Unconfirmed");
    },          
    findAllAsteroidByPhrase(phrase = '') {        
        ObjectsService.findAsteroidByPhrase(phrase, false, 'ovt', { includeFilters: false });
	},

    async findAsteroidByName()  {
        const insertList = ObjectsService.getInsertAsteroidsList();
        if (!insertList || insertList === '') {
            return;
        }
        SpinnerService.show();
        const url = ObjectsService.findAsteroidByNameUrl() + '?query=' + insertList;
        await axios(url)
            .then(response => {
                const objectsList = response.data.asteroids;
                const objectsErrors = response.data.errors;
                ObjectsService.setFinalAsteroidsList(objectsList);
                ObjectsService.setAsteroidsNames(ObjectsService.extractObjectsNames(objectsList));
                if (objectsErrors && objectsErrors.length) {
                    const successed = objectsList.length;
                    const errored = objectsErrors.length;
                    const found = successed + errored;
                    const message = `${successed} ${Keys.t.of} ${found} ${Keys.t.objectsFound}, ${errored} ${Keys.t.objectsNotFound}:<br />${objectsErrors.join(', ')}`;
                    setTimeout(() => {
                        PopupService.show({
                            component: 'PopupInfo',
                            type: 'warning',
                            message,
                        });
                    }, 0);          
                }
            })
            .catch(error => {
                const message = UtilsService.prepareErrorMessage(error);
                PopupService.show({
                    component: 'PopupInfo',
                    type: 'error',
                    message
                });
            })
            .finally(() => {
                SpinnerService.hide();
            });
	},

    updateObjectsFilteringValues(objects, overrideObjects) {
        if (!objects || !objects.length) return;
        const objectsRecent = ObjectsService.getObjects();
        const objectsList = objectsRecent.list;

        if (overrideObjects) {
            objectsRecent.total = objects.length;
            objectsRecent.list = objects;
            ObjectsService.setObjects(objectsRecent, true);
            ObjectsService.setUserDefinedObjects(true);
            const finalAsteroidsList = ObjectsService.getFinalAsteroidsList();
            const newFinalAsteroidList = [];
            for (const asteroid of finalAsteroidsList) {
                if (objectsRecent.list.some(e => e.name === asteroid.name)) {
                    newFinalAsteroidList.push(asteroid);
                }
            }
            ObjectsService.setFinalAsteroidsList(newFinalAsteroidList);
            MemoryService.saveSetupMemory();
        } else {
            for (const object of objects) {
                if (!object.error) {
                    const index = objectsList.findIndex(obj => obj.name === object.name);
                    objectsList[index].filters = object.filters;
                }
            }
        }

        FilteringService.setLastCalculatedEphemerides(true);
    },

    getDesignatorAndName(name) {
        const object = ObjectsService.getObjects().list.filter(e => e.name === name)[0];
        let designatorAndName = name;
        if (object.number) {    
            designatorAndName = `(${object.number}) ${object.name}`;
        }
        return designatorAndName;
    },

    objectNumberAndName(item) {
        const itemNumber = item.number || item.designator ? `(${item.number || item.designator}) ` : '';
        const itemName = item.name;
        return itemNumber + itemName;
    },

    displayObjectType(object) {
        if (ObjectsService.isCometWithoutObjectGroup(object)) {
            return object.bodyType.name;
        } else {
            if (object.objectGroup.name === 'N/A') { 
                return Keys.t.nonNeo + ' ' + object.bodyType.name;
            } else {
                return object.objectGroup.name + ' ' + object.bodyType.name;
            }
        }
    },

    isCometWithoutObjectGroup(object) {
        return (object.bodyType.name === 'Comet' || object.bodyType.name === 'JPL') && object.objectGroup.name === 'N/A';
    },
    isComet(object) {
        return (object.bodyType.name === 'Comet');
    },
}

export default ObjectsService;
